import { Colors } from "../../config/DesignConstants";
import { createRegularPolygon } from "../../core/util/PolygonUtils";
import { choose, rUniform } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { BaseEnemy } from "./BaseEnemy";

//=============================================================================
// ZEPHYR CONFIGURATION - All tweakable values in one place
//=============================================================================

const ZEPHYR_CONFIG = {
  // Basic stats
  health: 3,
  killPoints: 125, // Very high value target!
  mass: 0.2, // Light for quick movements
  size: 10,

  // Visual
  color: Colors.YELLOW,
  secondaryColor: Colors.ORANGE,
  strokeWidth: 3, // Thick stroke for visibility
  shape: createRegularPolygon(8, 10), // Golden octagon

  // Pulse animation
  pulseSpeedScale: 8, // Scale pulse frequency
  pulseSpeedAlpha: 10, // Alpha pulse frequency
  pulseAmountScale: 0.15, // Max scale variation
  pulseAmountAlpha: 0.3, // Max alpha variation
  pulseBaseAlpha: 0.7, // Base alpha level

  // Movement speeds
  enterSpeed: 180, // Very fast entry
  evadeSpeed: 150, // High speed evasion
  exitSpeed: 200, // Rapid escape

  // Timing
  evadeDuration: 10, // Seconds to evade before escaping
  patternSwitchTime: 1.5, // Switch patterns frequently
  maxEnterTime: 2, // Max time in entering phase
  enterDistance: 200, // Distance to center when switching to evade
  exitTime: 3, // Time before self-destruct when exiting

  // Pattern-specific settings
  chaos: {
    directionChangeMin: 0.2, // Min time before direction change
    directionChangeMax: 0.5, // Max time before direction change
  },

  figure8: {
    speed: 3, // Speed of figure-8 movement
    scale: 80, // Size of figure-8
    speedVariation: 0.3, // Speed variation amount
  },

  spiral: {
    reverseTime: 0.8, // Time before reversing spiral
    baseRadius: 50, // Base spiral radius
    radiusVariation: 30, // Radius variation
    angularSpeed: 4, // Angular velocity multiplier
  },

  stopAndGo: {
    stopDuration: 0.3, // Time to stay stopped
    moveDurationMin: 0.5, // Min time to move
    moveDurationMax: 0.8, // Max time to move
    speedMultiplier: 1.2, // Speed boost during movement
  },

  // Screen constraints
  screenMargin: 20, // Margin from screen edge for bouncing
} as const;

type ZephyrPhase = "entering" | "evading" | "exiting";
type EvadePattern = "chaos" | "figure8" | "spiral_reverse" | "stop_and_go";

//=============================================================================
// ZEPHYR ENEMY CLASS - Complex behavior made readable
//=============================================================================

export class ZephyrEnemy extends BaseEnemy {
  // State management
  private phase: ZephyrPhase = "entering";
  private phaseTimer = 0;
  private patternTimer = 0;
  private currentPattern: EvadePattern = "chaos";

  // Animation state
  private pulseTime = 0;

  // Movement state
  private targetDirection = V(0, 0);
  private patternTime = 0;
  private lastStopTime = 0;
  private isStopped = false;
  private spiralDirection = 1;

  constructor(position: V2d) {
    super(position, {
      health: ZEPHYR_CONFIG.health,
      killPoints: ZEPHYR_CONFIG.killPoints,
      mass: ZEPHYR_CONFIG.mass,
      visualConfig: {
        points: ZEPHYR_CONFIG.shape,
        color: ZEPHYR_CONFIG.color,
        secondaryColor: ZEPHYR_CONFIG.secondaryColor,
        strokeWidth: ZEPHYR_CONFIG.strokeWidth,
      },
    });

    // Initialize movement toward center
    this.targetDirection = V(0, 0).sub(position).normalize();
    this.selectNewPattern();
  }

  //===========================================================================
  // MAIN BEHAVIOR - All logic in one place, easy to read
  //===========================================================================

  onTick(dt: number): void {
    this.updatePulseAnimation(dt);
    this.updateMovement(dt);
  }

  private updatePulseAnimation(dt: number): void {
    this.pulseTime += dt;

    if (!this.vectorSprite?.sprite) return;

    // Pulsing scale effect to draw attention
    const pulseScale =
      1 +
      Math.sin(this.pulseTime * ZEPHYR_CONFIG.pulseSpeedScale) *
        ZEPHYR_CONFIG.pulseAmountScale;
    this.vectorSprite.sprite.scale.set(pulseScale, pulseScale);

    // Also pulse the alpha for extra sparkle
    const pulseAlpha =
      ZEPHYR_CONFIG.pulseBaseAlpha +
      Math.sin(this.pulseTime * ZEPHYR_CONFIG.pulseSpeedAlpha) *
        ZEPHYR_CONFIG.pulseAmountAlpha;
    this.vectorSprite.setAlpha(pulseAlpha);
  }

  private updateMovement(dt: number): void {
    if (!this.body) return;

    this.phaseTimer += dt;
    this.patternTime += dt;

    switch (this.phase) {
      case "entering":
        this.handleEntering();
        break;
      case "evading":
        this.handleEvading(dt);
        break;
      case "exiting":
        this.handleExiting();
        break;
    }
  }

  //===========================================================================
  // PHASE HANDLERS - Clear separation of different behaviors
  //===========================================================================

  private handleEntering(): void {
    // Move quickly toward center area
    const vel = this.targetDirection.mul(ZEPHYR_CONFIG.enterSpeed);
    this.body!.velocity = [vel.x, vel.y];

    // Switch to evading once we're in the play area
    const distanceFromCenter = this.getPosition().magnitude;
    if (
      distanceFromCenter < ZEPHYR_CONFIG.enterDistance ||
      this.phaseTimer > ZEPHYR_CONFIG.maxEnterTime
    ) {
      this.phase = "evading";
      this.phaseTimer = 0;
      this.patternTimer = 0;
    }
  }

  private handleEvading(dt: number): void {
    // Check if it's time to escape
    if (this.phaseTimer >= ZEPHYR_CONFIG.evadeDuration) {
      this.phase = "exiting";
      this.selectExitDirection();
      return;
    }

    // Switch patterns periodically
    this.patternTimer += dt;
    if (this.patternTimer >= ZEPHYR_CONFIG.patternSwitchTime) {
      this.selectNewPattern();
      this.patternTimer = 0;
      this.patternTime = 0;
    }

    // Execute current pattern
    this.executeCurrentPattern(dt);

    // Keep within screen bounds
    this.constrainToScreen();
  }

  private handleExiting(): void {
    // Accelerate in exit direction
    const vel = this.targetDirection.mul(ZEPHYR_CONFIG.exitSpeed);
    this.body!.velocity = [vel.x, vel.y];

    // Self-destruct after moving far enough off-screen
    if (this.phaseTimer > ZEPHYR_CONFIG.exitTime) {
      this.destroy();
    }
  }

  //===========================================================================
  // EVASION PATTERNS - Each pattern clearly isolated
  //===========================================================================

  private executeCurrentPattern(dt: number): void {
    switch (this.currentPattern) {
      case "chaos":
        this.executeChaosPattern();
        break;
      case "figure8":
        this.executeFigure8Pattern();
        break;
      case "spiral_reverse":
        this.executeSpiralReversePattern();
        break;
      case "stop_and_go":
        this.executeStopAndGoPattern();
        break;
    }
  }

  private executeChaosPattern(): void {
    const cfg = ZEPHYR_CONFIG.chaos;

    // Change direction randomly
    if (
      this.patternTime >
      rUniform(cfg.directionChangeMin, cfg.directionChangeMax)
    ) {
      const angle = rUniform(0, Math.PI * 2);
      this.targetDirection = V(Math.cos(angle), Math.sin(angle));
      this.patternTime = 0;
    }

    const vel = this.targetDirection.mul(ZEPHYR_CONFIG.evadeSpeed);
    this.body!.velocity = [vel.x, vel.y];
  }

  private executeFigure8Pattern(): void {
    const cfg = ZEPHYR_CONFIG.figure8;
    const t = this.patternTime * cfg.speed;

    // Lissajous curve for figure-8
    const vx = Math.cos(t) * cfg.scale;
    const vy = Math.sin(2 * t) * cfg.scale * 0.5;

    // Add speed variation
    const speedMod = 1 + Math.sin(t * 2) * cfg.speedVariation;

    this.body!.velocity = [vx * speedMod, vy * speedMod];
  }

  private executeSpiralReversePattern(): void {
    const cfg = ZEPHYR_CONFIG.spiral;

    // Reverse spiral direction periodically
    if (this.patternTime > cfg.reverseTime) {
      this.spiralDirection *= -1;
      this.patternTime = 0;
    }

    const t = this.phaseTimer * cfg.angularSpeed;
    const radius = cfg.baseRadius + Math.sin(t) * cfg.radiusVariation;
    const angle = t * 2 * this.spiralDirection;

    const vx = Math.cos(angle) * radius;
    const vy = Math.sin(angle) * radius;

    this.body!.velocity = [vx, vy];
  }

  private executeStopAndGoPattern(): void {
    const cfg = ZEPHYR_CONFIG.stopAndGo;

    if (this.isStopped) {
      // Stop for specified duration
      if (this.patternTime - this.lastStopTime > cfg.stopDuration) {
        this.isStopped = false;
        // Pick new random direction for burst
        const angle = rUniform(0, Math.PI * 2);
        this.targetDirection = V(Math.cos(angle), Math.sin(angle));
      } else {
        this.body!.velocity = [0, 0];
      }
    } else {
      // Move for random duration then stop
      if (
        this.patternTime - this.lastStopTime >
        rUniform(cfg.moveDurationMin, cfg.moveDurationMax)
      ) {
        this.isStopped = true;
        this.lastStopTime = this.patternTime;
      } else {
        const vel = this.targetDirection.mul(
          ZEPHYR_CONFIG.evadeSpeed * cfg.speedMultiplier,
        );
        this.body!.velocity = [vel.x, vel.y];
      }
    }
  }

  //===========================================================================
  // UTILITY METHODS - Support functions
  //===========================================================================

  private selectNewPattern(): void {
    this.currentPattern = choose(
      "chaos",
      "figure8",
      "spiral_reverse",
      "stop_and_go",
    );
  }

  private selectExitDirection(): void {
    if (!this.game) return;

    // Exit toward nearest screen edge
    const pos = this.getPosition();
    const viewport = this.game.camera.getWorldViewport();

    const distances = [
      { dir: V(0, -1), dist: pos.y - viewport.top }, // Up
      { dir: V(0, 1), dist: viewport.bottom - pos.y }, // Down
      { dir: V(-1, 0), dist: pos.x - viewport.left }, // Left
      { dir: V(1, 0), dist: viewport.right - pos.x }, // Right
    ];

    // Find closest edge
    distances.sort((a, b) => a.dist - b.dist);
    this.targetDirection = distances[0].dir;
  }

  private constrainToScreen(): void {
    if (!this.body || !this.game) return;

    const viewport = this.game.camera.getWorldViewport();
    const margin = ZEPHYR_CONFIG.screenMargin;
    const pos = V(this.body.position);
    const vel = V(this.body.velocity);

    // Bounce off screen edges
    if (pos.x <= viewport.left + margin && vel.x < 0) {
      this.body.velocity[0] = Math.abs(vel.x);
    } else if (pos.x >= viewport.right - margin && vel.x > 0) {
      this.body.velocity[0] = -Math.abs(vel.x);
    }

    if (pos.y <= viewport.top + margin && vel.y < 0) {
      this.body.velocity[1] = Math.abs(vel.y);
    } else if (pos.y >= viewport.bottom - margin && vel.y > 0) {
      this.body.velocity[1] = -Math.abs(vel.y);
    }
  }
}
